import React, { useMemo, useState } from "react";
import { makeAPI } from "../../services/api";

const REWARD_TYPES = [
  { value: "1", label: "Coupon" },
  { value: "2", label: "Gift Card" },
  { value: "3", label: "Others" },
];

export default function RewardSponser() {
  document.title = "Reward Sponsor - CGES.CLUB";

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    brandName: "",
    contactPerson: "",
    email: "",
    number: "",
    brandUrl: "",
    rewardType: "",
    otherReward: "",
    rewardValue: "",
    message: "",
  });

  const showOtherReward = useMemo(() => formData.rewardType === "3", [formData.rewardType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, number: onlyDigits }));
    setErrors((prev) => ({ ...prev, number: "" }));
  };

  const scrollToFirstError = () => {
    const firstInvalid = document.querySelector(".is-invalid");
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalid.focus();
    }
  };

  const validate = () => {
    const nextErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.brandName.trim() || formData.brandName.trim().length > 50) {
      nextErrors.brandName = "Enter a valid brand name (max 50 characters).";
    }

    if (!formData.contactPerson.trim() || formData.contactPerson.trim().length > 50) {
      nextErrors.contactPerson = "Enter a valid contact person name (max 50 characters).";
    }

    if (!emailRegex.test(formData.email) || formData.email.length > 100) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!/^\d{10}$/.test(formData.number)) {
      nextErrors.number = "Please enter a valid 10-digit mobile number.";
    }

    if (!formData.brandUrl || formData.brandUrl.length > 2000) {
      nextErrors.brandUrl = "Please enter a valid website link.";
    }

    if (!formData.rewardType) {
      nextErrors.rewardType = "Please select reward type.";
    }

    if (showOtherReward) {
      const trimmedOther = formData.otherReward.trim();
      if (!trimmedOther || trimmedOther.length > 30) {
        nextErrors.otherReward = "Specify other reward type (max 30 characters).";
      }
    }

    const rewardVal = Number(formData.rewardValue);
    if (!rewardVal || rewardVal < 1 || rewardVal > 100000000) {
      nextErrors.rewardValue = "Enter a valid approximate reward value.";
    }

    if (formData.message.length > 150) {
      nextErrors.message = "Only 150 characters are allowed.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      scrollToFirstError();
      return;
    }

    setErrors({});
    setSubmitting(true);

    const rewardTypeLabel =
      REWARD_TYPES.find((item) => item.value === formData.rewardType)?.label || "Invalid Value";

    const payload = {
      brand_name: formData.brandName.trim(),
      contact_person: formData.contactPerson.trim(),
      email: formData.email.trim(),
      phone_number: Number(formData.number),
      brand_url: formData.brandUrl.trim(),
      reward_type: rewardTypeLabel,
      reward_value: Number(formData.rewardValue),
      message: formData.message.trim(),
    };

    if (showOtherReward) {
      payload.other_reward = formData.otherReward.trim();
    }

    try {
      const response = await makeAPI("/api/sponser_registration", "POST", false, payload);
      if (response?.status === "S") {
        setSubmitted(true);
      } else {
        alert("Failed to submit. Please try again.");
      }
    } catch (error) {
      console.error("Reward sponsor submit failed:", error);
      alert("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
        .reward-shell {
          max-width: 980px;
         
        }

        .dark-card {
          background-color: #161616;
          color: #e4e4e4;
          border: 1px solid #2a2a2a;
          border-radius: 16px;
        }

        .dark-shadow {
          box-shadow: 0 16px 32px rgba(0, 0, 0, 0.35);
        }

        .form-head-block {
          padding-bottom: 16px;
          margin-bottom: 18px;
          border-bottom: 1px solid #2f2f2f;
        }

        .form-head-block h1 {
          font-size: clamp(1.2rem, 2.8vw, 1.9rem);
          font-weight: 700;
          margin: 0 0 10px;
          color: #ffffff;
        }

        .form-head-block p {
          margin: 0;
          color: #d6d6d6;
          line-height: 1.7;
          font-size: clamp(0.9rem, 1.8vw, 1.02rem);
        }

        .section-title {
          color: #ff810e;
          border-bottom: 1px solid #2f2f2f;
          padding-bottom: 8px;
          margin-top: 0.5rem !important;
          margin-bottom: 1rem;
        }

        .dark-card form label {
          display: inline-block;
          margin-bottom: 0.45rem;
          line-height: 1.4;
          font-weight: 500;
        }

        .dark-input {
          background-color: #000 !important;
          color: #e4e4e4 !important;
          border: 1px solid #333333;
          min-height: 44px;
        }

        .dark-input::placeholder {
          color: #797979 !important;
          opacity: 1;
        }

        .dark-input:focus {
          border-color: #ff810e;
          box-shadow: 0 0 0 0.18rem rgba(255, 129, 14, 0.2);
        }

        .help-text {
          color: #a8a8a8;
          font-size: 0.84rem;
          margin-top: 0.45rem;
        }

        .dark-card form .mb-3 {
          margin-bottom: 1rem !important;
        }

        .btn-orange {
          background-color: #ff810e;
          border: none;
          color: #fff;
          font-weight: 600;
          min-height: 44px;
          padding: 8px 20px;
          margin-top: 1.2rem !important;
        }

        .btn-orange:hover {
          background-color: #e6730c;
          color: #fff;
        }

        .btn-submit-wrap {
          margin-top: 1.2rem;
          text-align: center;
        }

        .btn-submit-wrap .btn-orange {
          width: auto;
          min-width: 140px;
        }

        .thank-you-box {
          background: #161616;
          color: #e4e4e4;
          padding: 40px 24px;
          border-radius: 10px;
          text-align: center;
          border: 1px solid #2f2f2f;
        }

        .thank-you-box h3 {
          color: #ff810e;
        }

        @media (max-width: 768px) {
          .reward-shell {
          
            margin-bottom: 1.5rem !important;
          }

          .dark-card {
            padding: 18px !important;
          }

          .form-head-block {
            margin-bottom: 14px;
            padding-bottom: 12px;
          }
        }
        `}
      </style>

      <div className="container mt-5 mb-5 reward-shell">
        <div className="card dark-card dark-shadow p-4">
          <div className="form-head-block">
            <h1>Reward Sponsor Registration</h1>
            <p>
              Partner with CGES.CLUB by sponsoring coupons, gift cards, and exclusive rewards for
              tournaments and challenges. Share your brand details and collaboration idea below.
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} noValidate>
              <h5 className="section-title">Sponsor Details</h5>

              <div className="mb-3">
                <label htmlFor="brandName">Brand Name *</label>
                <input
                  id="brandName"
                  name="brandName"
                  type="text"
                  className={`form-control dark-input ${errors.brandName ? "is-invalid" : ""}`}
                  placeholder="Brand Name"
                  minLength={1}
                  maxLength={50}
                  value={formData.brandName}
                  onChange={handleChange}
                  required
                />
                {errors.brandName && <div className="invalid-feedback">{errors.brandName}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="contactPerson">Contact Person *</label>
                <input
                  id="contactPerson"
                  name="contactPerson"
                  type="text"
                  className={`form-control dark-input ${errors.contactPerson ? "is-invalid" : ""}`}
                  placeholder="Contact Person"
                  minLength={1}
                  maxLength={50}
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                />
                {errors.contactPerson && (
                  <div className="invalid-feedback">{errors.contactPerson}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`form-control dark-input ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Email"
                  maxLength={100}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="number">Mobile Number *</label>
                <input
                  id="number"
                  name="number"
                  type="tel"
                  inputMode="numeric"
                  className={`form-control dark-input ${errors.number ? "is-invalid" : ""}`}
                  placeholder="10-digit mobile number"
                  value={formData.number}
                  onChange={handlePhoneChange}
                  required
                />
                {errors.number && <div className="invalid-feedback">{errors.number}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="brandUrl">Website Link *</label>
                <input
                  id="brandUrl"
                  name="brandUrl"
                  type="url"
                  className={`form-control dark-input ${errors.brandUrl ? "is-invalid" : ""}`}
                  placeholder="https://yourbrand.com"
                  maxLength={2000}
                  value={formData.brandUrl}
                  onChange={handleChange}
                  required
                />
                {errors.brandUrl && <div className="invalid-feedback">{errors.brandUrl}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="rewardType">Type of Reward *</label>
                <select
                  id="rewardType"
                  name="rewardType"
                  className={`form-select dark-input ${errors.rewardType ? "is-invalid" : ""}`}
                  value={formData.rewardType}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select reward type
                  </option>
                  {REWARD_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {errors.rewardType && <div className="invalid-feedback">{errors.rewardType}</div>}
              </div>

              {showOtherReward && (
                <div className="mb-3">
                  <label htmlFor="otherReward">Specify Other Reward Type *</label>
                  <input
                    id="otherReward"
                    name="otherReward"
                    type="text"
                    className={`form-control dark-input ${errors.otherReward ? "is-invalid" : ""}`}
                    placeholder="Specify reward type"
                    maxLength={30}
                    value={formData.otherReward}
                    onChange={handleChange}
                    required={showOtherReward}
                  />
                  {errors.otherReward && (
                    <div className="invalid-feedback">{errors.otherReward}</div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="rewardValue">Approximate Reward Value *</label>
                <input
                  id="rewardValue"
                  name="rewardValue"
                  type="number"
                  className={`form-control dark-input ${errors.rewardValue ? "is-invalid" : ""}`}
                  placeholder="Enter reward value"
                  min={1}
                  max={100000000}
                  value={formData.rewardValue}
                  onChange={handleChange}
                  required
                />
                {errors.rewardValue && <div className="invalid-feedback">{errors.rewardValue}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="message">Message / Collaboration Idea</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  maxLength={150}
                  className={`form-control dark-input ${errors.message ? "is-invalid" : ""}`}
                  placeholder="Describe your message or collaboration idea"
                  value={formData.message}
                  onChange={handleChange}
                />
                <div className="help-text">Maximum 150 characters.</div>
                {errors.message && <div className="invalid-feedback">{errors.message}</div>}
              </div>

              <div className="btn-submit-wrap">
                <button type="submit" className="btn btn-orange" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          ) : (
            <div className="thank-you-box">
              <h3>Thank You</h3>
              <p>Your reward sponsor form has been submitted successfully.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
